/**
 * @email       : saraman@ibl.mx
 * @author      : Arman Ali
 * @title       : Full Stack Developer
 * @github      : https://github.com/Arman-Arzoo
 * @hook        : ApprovalFlowApp
 * @description : Custom hook for managing the state and logic related to the approval flow.
 *                This includes functions for adding and deleting approval levels, managing
 *                approvers, handling input changes, and submitting approval flow data through an API.
 *
 * @dependencies:
 * - Redux (for dispatching actions like CreateApproval)
 * - js-cookie (for getting the selected language)
 * - i18next (for translations)
 *
 * @returns {Object} An object containing state and functions for managing approval flow:
 *   - t                             : Translation function
 *   - addApprovalLevel              : Function to add a new approval level
 *   - deleteApprovalLevel           : Function to delete an approval level
 *   - addApprover                   : Function to add an approver to a level
 *   - deleteApprover                : Function to delete an approver from a level
 *   - handleInputChange             : Function to handle input changes for approvers
 *   - handleRuleChange              : Function to handle rule changes for approval levels
 *   - handleApprovalFlowApi         : Function to dispatch the approval flow creation API
 *   - deleteModal                   : Modal visibility state for approval deletion
 *   - setDeleteModal                : Function to toggle the delete modal
 *   - deleteId                      : ID for the approval to delete
 *   - setDeleteId                   : Function to set the approval delete ID
 *   - setModule                     : Function to set the module
 *   - module                        : State for the selected module
 *   - department                    : State for the selected department
 *   - setDepartment                 : Function to set the department
 *   - lCode                         : Language code from cookies
 *   - DEV                           : Development mode flag from Redux
 *   - emptyFields                   : State to track empty fields
 *   - levels                        : State for approval levels
 *   - name                          : Approval flow name state
 *   - setName                       : Function to set the approval flow name
 *   - permission                    : User permission list from Redux
 */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import {
    CreateApproval,
    GetApprovalById,
    GetListApprovalModules,
    UpdateApproval
} from '../../reduxToolkit/Approval/ApprovalApi';
import { GetAllDepartments } from '../../reduxToolkit/DocumentPanel/DocumentPanelApi';
import { useLocation, useNavigate } from 'react-router-dom';
import routes from '../../Routes/routes';
import { toast } from 'react-toastify';
import {APPROVAL_MODEL, LEVEL_MODEL} from "./Model/Model";
import HandleChange from "../../Helpers/HandleChange";
import {errorMsg} from "../../Apis/functions";

const ApprovalFlowApp = () => {
    const [levels, setLevels] = useState([]);
    const [deleteModal, setDeleteModal] = useState(false);
    const [deleteId, setDeleteId] = useState('');
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const [emptyFields, setEmptyFields] = useState(false);

    const DEV = useSelector((state) => state?.CommonsSlice?.development);
    const { permission } = useSelector((state) => state.authenticatioauthennSlice);
    const { getAllDepartments } = useSelector((state) => state.DocumentPanelSlice);

    const { getListApprovalModules } = useSelector((state) => state?.ApprovalSlice);
    const location = useLocation();
    const navigate = useNavigate();
    let { state } = location; // Get state from location
    let approvalId = state?.id; // Retrieve contractor ID from the state passed via navigation
    const [item, setItem] = useState({...APPROVAL_MODEL});
    const { handleChange } = HandleChange({ model: item, setModel: setItem });
    const [cleanChipSearch, setCleanChipSearch] = useState(false);

    useEffect(() => {
        dispatch(GetAllDepartments());
        dispatch(GetListApprovalModules());
    }, []);

    useEffect(() => {
        if(approvalId){
            dispatch(GetApprovalById(approvalId)).then(({payload:{data:{data}}}) => {
                if(data){
                    setItem({...APPROVAL_MODEL.set(data)});
                    setLevels(LEVEL_MODEL.set(data?.levels));
                }
            });
        }
    }, [approvalId]);


    const handleApprovalFlowApi = () => {
        setCleanChipSearch(true);
        if (!item.isValid() || levels.length === 0 || !LEVEL_MODEL.isValid(levels)) {
            setEmptyFields(true);
            if(levels.length === 0) toast.warning(t("add_at_least_one_level"));
            return;
        }

        const aux = item.toRegister(levels);
        const action = approvalId ? UpdateApproval(aux) : CreateApproval(aux);

        dispatch(action).then(({ payload: { data: { data, code, success} } }) => {
            if (success) {
                if (approvalId) {
                    navigate(location.pathname, { replace: true, state: {...state, id: data.id} });
                    toast.success(t('has_been_updated_successfully'));
                } else {
                    navigate(routes.approval.list);
                    toast.success(t('has_been_created_successfully'));
                }
            } else {
                errorMsg(code);
            }
        });
    };

    //  add approval level function
    const addApprovalLevel = () => {
        const newLevel = { ...LEVEL_MODEL, levelNumber: levels.length + 1};
        setLevels([...levels, newLevel]);
    };

    const addApprover = (levelId, newList) => {
        setLevels((prevLevels) =>
            prevLevels.map((level) => {
                if (level.levelNumber === levelId) {
                    return {
                        ...level,
                        approvers: newList,
                    };
                }
                return level;
            }),
        );
    };

    //  delete approver  function
    const deleteApprover = (levelId, i) => {
        setLevels((prevLevels) =>
            prevLevels.map((level) =>
                level.levelNumber === levelId
                    ? {
                        ...level,
                        approvers: level.approvers.filter((_, index) => index !== i),
                    }
                    : level,
            ),
        );
    };

    //  delete approval level function
    const deleteApprovalLevel = (levelId) => {
        setLevels(levels.filter((level) => level.levelNumber !== levelId).map((e, index)=> {
            e.levelNumber = index+1;
            return e;
        }));
    };

    // rule change
    const handleRuleChange = (levelId, newRule) => {
        setLevels((prevLevels) =>
            prevLevels.map((level) =>
                level.levelNumber === levelId ? { ...level, approvalCondition: newRule } : level,
            ),
        );
    };

    const handleClear = () => {
        setItem({...APPROVAL_MODEL});
        setEmptyFields(false);
        setLevels([]);
    };


    return {
        DEV,
        emptyFields,
        t,
        setDeleteModal,
        levels,
        deleteId,
        setDeleteId,
        deleteModal,
        permission,
        getAllDepartments,
        getListApprovalModules,
        approvalId,
        item,
        handleChange,
        cleanChipSearch,
        setCleanChipSearch,
        deleteApprovalLevel,
        handleApprovalFlowApi,
        handleRuleChange,
        deleteApprover,
        addApprover,
        addApprovalLevel,
        handleClear
    };
};

export default ApprovalFlowApp;
